/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ViewBase } from '../view-base.js';
import { baseGuildViewStyles } from './base-guild-view-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-guild-card/q1-guild-card.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== Q1 MODULES ===*/
import { q1DocFunctions } from '../../modules/q1-core-functions.js';
import { enrichGuild } from '../../modules/guild-helpers-client.js';

/*=== CONSTANTS ===*/

const ALL_GUILDS_CACHE_ID = 'all-guilds-search';

const CACHE_MAX_AGE = 10; // minutes

/*=== VIEW MIXIN CLASS ===*/

export class BaseGuildView extends ViewBase {
  static styles = [
    ...super.styles,
    baseGuildViewStyles,
  ];

  static properties = {
    ...super.properties,

    /**
     * Values:
     * - `init`: Starting state, shows page load skeleton
     * - `not-found`: Displays 404-like page
     * - `not-visible`: Displays error message around permissions
     * - `loaded`: Displays loaded guild
     * - `load-error`: Displays error message in notice panel. This is different from `this.error`.
     *   The error boolean can be true in any view mode. Load error is specific to errors which
     *   prevent the original load from succeeding.
     */
    viewMode: { type: String },
    darkMode: { type: Boolean },

    guildKey: { type: String },
    guild: { type: Object },
    context: { type: Object },

    loading: { type: Boolean },
    error: { type: Boolean },
    errorMessage: { type: String },
  };

  constructor() {
    super();

    // Begin by creating a `this.initComplete` promise that resolves when the view is initialized.
    this.resetInitParams();
    
    this.viewMode = 'init';
    this.darkMode = false; // experimental

    this.guildKey = null;
    this.guild = null;
    this.context = null;
    
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
  }

  connectedCallback() {
    super.connectedCallback();

    this._loadGuildAndSetViewMode();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('routeParams')) {
      this._loadGuildAndSetViewMode();
    }
    if (changedProperties.has('darkMode')) {
      if (this.darkMode) this.classList.add('dark-mode');
      else this.classList.remove('dark-mode');
    }
  }

  /** This is meant to be called when we want to refresh the guild if the params have changed. */
  async _loadGuildAndSetViewMode() {
    const newGuildKey = this.routeParams?.key;
    if ((this.guildKey === newGuildKey) || !newGuildKey) return;
    
    this.guildKey = newGuildKey;
    return this._refreshGuild();
  }

  /**
   * Overwrite this method to kick off a process right alongside the guild load.
   * 
   * This method is SYNCHRONOUS and will not hold up the guild load.
   */
  guildLoadStarted() {}

  /**
   * Overwrite this method to do something after the guild is loaded, but before the `loading`
   * boolean is set to false. Throw an error to set the view mode to `load-error`.
   * Or just overwrite the view mode to do something else.
   * 
   * This method is ASYNCHRONOUS and WILL hold up the view initialization.
   * 
   * You can use this._loadError_* methods to set the view mode to load error if needed.
   */
  async guildLoaded() {}

  /** Call this to set the css styles from source object */
  _setGuildStyles(sourceObject = this.guild, targetElement = this) {
    if (sourceObject) {
      targetElement.style.setProperty('--theme-color-primary', sourceObject.colorPrimary);
      targetElement.style.setProperty('--theme-color-secondary', sourceObject.colorSecondary);
      targetElement.style.setProperty('--guild-overlay-opacity', sourceObject.overlayOpacity);
      targetElement.style.setProperty('--guild-banner-opacity', sourceObject.bannerOpacity);
      targetElement.style.setProperty('--theme-text-color-primary', sourceObject.textColorPrimary_hex);
      targetElement.style.setProperty('--theme-text-color-secondary', sourceObject.textColorSecondary_hex);
    } else {
      targetElement.style.setProperty('--theme-color-primary', 'var(--teal-7)');
      targetElement.style.setProperty('--theme-color-secondary', 'var(--teal-7)');
      targetElement.style.setProperty('--guild-overlay-opacity', '0.5');
      targetElement.style.setProperty('--guild-banner-opacity', '0.5');
      targetElement.style.setProperty('--theme-text-color-primary', 'white');
      targetElement.style.setProperty('--theme-text-color-secondary', 'white');
    }
  }

  /** This is meant to be called when we want to refresh the guild no matter what. */
  async _refreshGuild() {
    this.loading = true;
    this.error = false;
    this.errorMessage = '';
    
    const { getDoc } = q1DocFunctions;
    
    try {
      this.guildLoadStarted(); // Allow for custom logic to kick off in parallel
      const response = await getDoc({ docCollection: 'guilds', docKey: this.guildKey });
      const responseData = response.data;
      const { docData: guild, currentMemberContext } = responseData;
      
      this.guild = enrichGuild(guild);
      this.context = currentMemberContext;
      this._setGuildStyles();
      this.setAfLink_db('guilds', this.guild.id);

      // Check for non-visible w/o error (that means not private, so basic details can be shown).
      // But it still counts as an error state for our purposes.
      if (!this.context.canView) {
        this.viewMode = 'not-visible';
        this.setViewMeta(this.docTitle_partiallyVisible);
      } else {
        // Call the guildLoaded method to allow the child page to set title & insert custom logic
        // Use this._loadError_* methods to set the view mode to load error if needed.
        await this.guildLoaded();
        this.viewMode = 'loaded';
        this._resolveInit(); // resolves the `this.initComplete` promise
      }
    } catch (error) {
      console.error('Error Loading Guild:', error);

      switch (error.details?.resultCode) {
        case 'redirect-to-detail-view':
          this.showToast(error.message, null, 'danger');
          this.navigateTo(`/${this.guildKey}`);
          break;
          
        case 'not-found':
          this.setErrorViewMeta('Guild Not Found', 'There is no guild with that key.');
          this.viewMode = 'not-found';
          break;

        case 'view-not-allowed':
          this.setErrorViewMeta('Private Guild', 'This guild is private and not visible to you.');
          this.viewMode = 'not-visible';

          // If this is a private guild and the member is signed in, then we can retrieve the
          // basic details of the guild from the search cache.
          if (this.isSignedIn) await this._getPrivateGuildDetailsFromCache();
          this._setGuildStyles();

          break;

        default:
          this.setErrorViewMeta(
            'Guild Load Error', 
            error.message || 'Something went wrong while loading this guild.'
          );
          this.error = true;
          this.errorMessage = error.message;
          this.viewMode = 'load-error';
          break;
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * This will redirect to the guild detail view and display the error in a toast.
   * DO NOT USE THIS IN THE ROOT GUILD DETAIL VIEW, it will cause an infinite loop.
   */
  _loadError_redirectToDetailView(errorMessage) {
    const error = new Error(errorMessage);
    error.details = { resultCode: 'redirect-to-detail-view' };
    throw error;
  }

  /** Only used for signed in members to retrieve the basic guild details when private. */
  async _getPrivateGuildDetailsFromCache() {
    if (!this.isSignedIn || this.guild || !this.guildKey) return;

    try {
      let allGuildsCache = 
        await this.appElement.cacheManager.getCache(ALL_GUILDS_CACHE_ID, CACHE_MAX_AGE);
      if (allGuildsCache.isNewOrStale) allGuildsCache = await allGuildsCache.refreshComplete;
      
      const guildId = allGuildsCache.body?.guildIdsByKey?.[this.guildKey];
      const guildData = allGuildsCache.data?.guilds?.[guildId];

      if (guildData) this.guild = enrichGuild(guildData);
    } catch (error) {
      console.error('Error retrieving guild cache:', error);
      return;
    }
  }

  /*=== GETTERS ===*/

  /**
   * Overwrite this to change the document title for partial visibility, which is handled by the
   * not found template but has access to the basic guild details. (this.guild is non-null)
   */
  get docTitle_partiallyVisible() {
    return `${this.guild.name} - q1 Network`;
  }

  /** Overwriting this from view-base to conditionally set white text on q1-app-header. */
  get appHeaderVariant() {
    // NOTE: The very top of the view content is the fully saturated primary color, so this setting
    // tells us whether we need white text to contrast with that.
    if (!this.guild) return 'white-text';
    return this.guild?.textColorPrimary_isWhite ? 'white-text' : 'default';
  }

  get appFooterVariant() {
    return this.darkMode ? 'white-text' : 'default';
  }

  get viewContentStyleClasses() {
    return this.darkMode ? 'dark-mode' : '';
  }

  get guildId() {
    return this.guild?.id;
  }

  get isActivationComplete() {
    return ['activated', 'launched', 'retired'].includes(this.guild?.activationStatus);
  }
  
  get isSeeded() {
    return ['seeded', 'activated', 'launched', 'retired'].includes(this.guild?.activationStatus);
  }

  get canManageGuild() {
    return this.context?.canEdit || false;
  }

  get isGuildAdmin() {
    return this.context?.role === 'point';
  }

  get guildUrl() {
    if (!this.guild) return '';
    return `${location.origin}/${this.guild.key}`;
  }

  /*=== TEMPLATES ===*/

  get contentTemplate() {
    return html`
      ${this.contentHeaderTemplate}

      ${this.viewModeTemplate}

      <q1-spinner-panel label="Loading..." ?open=${this.loading}></q1-spinner-panel>
    `;
  }

  get contentHeaderTemplate() {
    if (['init', 'loaded'].includes(this.viewMode)) return null;

    return html`
      <q1-guild-card
        .guild=${this.guild}
        guild-key=${this.guildKey}
        class="guild-error"
        always-active
        no-bg
      ></q1-guild-card>
    `;
  }
  
  get viewModeTemplate() {
    switch (this.viewMode) {
      case 'init':
        return this.viewModeTemplate_init;

      case 'not-found':
        return this.viewModeTemplate_notFound;

      case 'not-visible':
        return this.viewModeTemplate_notVisible;

      case 'load-error':
        return this.viewModeTemplate_loadError;
        
      case 'loaded':
        return this.viewModeTemplate_loaded;

      default:
        this.errorMessage = `Unknown view mode: ${this.viewMode}`;
        this.error = true;
        return this.viewModeTemplate_loadError;
    }
  }

  get viewModeTemplate_init() {
    // IMPROVEME: Add skeleton loading view
    return null;
  }

  get viewModeTemplate_notFound() {
    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="block"></sl-icon>

          <h2>Guild not found...</h2>

          <p>
            Please check the URL and try again.
          </p>
        </div>
      </notice-panel>
    `;
  }

  get notVisibleIcon() {
    if (!this.isSignedIn) return this.guild 
      ? 'hub' // signed out, guild rendered = link private
      : 'lock'; // signed out, no guild rendered = fully private
    if (this.guild) return 'lock'; // signed in and viewing the cached version
    return 'visibility_off'; // no cached version for some reason
  }

  get notVisibleTitle() {
    if (!this.isSignedIn) return this.guild
      ? 'This guild is only visible to q1 Network members'
      : 'This is a private guild';
    if (this.guild) return 'This is a private guild';
    return 'You do not have permission to view this guild';
  }

  get notVisibleMessage() {
    if (!this.isSignedIn) {
      // link private
      if (this.guild) return html`
        <p>
          If you want to explore this guild,
          the first step is joining the q1 Network!
        </p>

        ${this.ctaRegisterTemplate}
      `;

      // fully private
      return html`
        <p>
          If you're interested in getting involved,
          it's probably a good idea to join the q1 Network.
          Click the button below to register.
        </p>

        ${this.ctaRegisterTemplate}
      `;
    }

    // Fully private, found in guild search cache
    if (this.guild) return html`
      <p>
        If you're interested in this guild, try reaching out to the organizers in Slack.
      </p>
    `;

    // Fully private, not found in guild search cache
    return html`Reach out to the foundation team if you have any questions.`; // shouldn't happen
  }

  get ctaRegisterTemplate() {
    return html`
      <sl-button
        variant="primary"
        size="large"
        href="/register"
        pill
      >Register Now</sl-button>

      <p class="footer">
        Already a member?
        <a href="/signin">Sign in here</a>.
        <br/>
        Curious about the q1 Network?
        <a href="/join">Learn more about how it works</a>.
      </p>
    `;
  }

  get viewModeTemplate_notVisible() {
    return html`
      <notice-panel class=${classMap({
        hero: true,
        'primary': !!this.guild,
        'sl-themed-secondary': !!this.guild,
      })}>
        <div class="inner">
          <sl-icon library="material" name=${this.notVisibleIcon}></sl-icon>

          <h2>${this.notVisibleTitle}</h2>

          ${this.notVisibleMessage}
      </notice-panel>
    `;
  }
  
  get viewModeTemplate_loadError() {
    return html`
      <notice-panel class="hero danger">
        <div class="inner">
          <sl-icon library="material" name="error"></sl-icon>

          <h2>An error occurred!</h2>

          <p>
            ${this.errorMessage}
          </p>
        </div>
      </notice-panel>
    `;
  }

  /** Use this for displaying IN a view mode template other than load error. */
  get errorAlertTemplate() {
    if (!this.error) return null;

    return html`
      <sl-alert variant="danger" open>
        <sl-icon library="material" name="report" slot="icon"></sl-icon>
        <strong>An error occurred!</strong><br/>
        ${this.errorMessage}
      </sl-alert>
    `;
  }

}
